import React, { useState, useRef } from 'react';
import { MapPin, ChevronDown, ChevronUp, CalendarClock } from 'lucide-react';
import { useFilters } from '../../hooks/useFilters';
import { Input } from '../Common/Input';
import { TimeInput } from '../Common/TimeInput';
import AddressInput from '../Common/AddressInput';
import { IconHeader } from '../Common/Dialog';
import { DEFAULT_SEARCH_ADDRESS } from '../../context/FilterContext';
import { trackEvent } from '../../services/analytics';

const parseAddress = (addr) => {
  if (!addr) return { street: '', plz: '', city: '' };
  const parts = addr.split(', ');
  if (parts.length >= 2) {
    const street = parts[0];
    const rest = parts.slice(1).join(', ');
    const spaceIdx = rest.indexOf(' ');
    if (spaceIdx > 0 && /^\d{4,5}$/.test(rest.substring(0, spaceIdx))) {
      return { street, plz: rest.substring(0, spaceIdx), city: rest.substring(spaceIdx + 1) };
    }
    return { street, plz: '', city: rest };
  }
  return { street: addr, plz: '', city: '' };
};

const LocationFilter = ({ autoExpand = false }) => {
  const { filters, updateFilter } = useFilters();
  // Bei autoExpand direkt aufklappen und die Felder aus der aktuellen Adresse vorbelegen
  const initial = autoExpand ? parseAddress(filters.start_address) : { street: '', plz: '', city: '' };
  const [expanded, setExpanded] = useState(autoExpand);
  const [street, setStreet] = useState(initial.street);
  const [plz, setPlz] = useState(initial.plz);
  const [city, setCity] = useState(initial.city);
  const [isChecking, setIsChecking] = useState(false);
  const validateRef = useRef(null);

  const handleToggle = () => {
    if (!expanded) {
      const parsed = parseAddress(filters.start_address);
      setStreet(parsed.street);
      setPlz(parsed.plz);
      setCity(parsed.city);
    }
    setExpanded(v => !v);
  };

  const handleConfirm = async () => {
    const plzCity = [plz.trim(), city.trim()].filter(Boolean).join(' ');
    const combined = [street.trim(), plzCity].filter(Boolean).join(', ');
    if (!combined) {
      updateFilter('start_address', '');
      setExpanded(false);
      return;
    }
    if (validateRef.current) {
      const error = await validateRef.current();
      if (!error) {
        updateFilter('start_address', combined);
        trackEvent('address-change', {
          is_default: combined.toLowerCase() === DEFAULT_SEARCH_ADDRESS.toLowerCase(),
        });
        setExpanded(false);
      }
    }
  };

  const displayAddress = filters.start_address || 'Adresse eingeben';
  const hasAddress = !!filters.start_address;

  return (
    <div className="filter">
      <IconHeader Icon={<CalendarClock size={20} color="#fff" />} info={''}>
        Startpunkt & Startzeit
      </IconHeader>
      <div className="filter__box">
        <div className="address-expand">
          <button
            type="button"
            className={`address-expand__toggle${hasAddress ? ' address-expand__toggle--filled' : ''}`}
            onClick={handleToggle}
            aria-expanded={expanded}
          >
            <MapPin size={16} className="address-expand__pin" />
            <span className="body Font-M-I-130-T Colors-Black address-expand__label">{displayAddress}</span>
            {expanded
              ? <ChevronUp size={16} className="address-expand__chevron" />
              : <ChevronDown size={16} className="address-expand__chevron" />}
          </button>

          {expanded && (
            <div className="address-expand__form">
              <AddressInput
                street={street}
                plz={plz}
                city={city}
                onChange={({ street: s, plz: p, city: c }) => {
                  setStreet(s);
                  setPlz(p);
                  setCity(c);
                }}
                onValidationChange={(status) => setIsChecking(status === 'checking')}
                validateRef={validateRef}
                geolocation
              />

              <button
                type="button"
                className="btn btn-primary address-expand__confirm"
                onClick={handleConfirm}
                disabled={isChecking}
              >
                {isChecking ? 'Wird geprüft…' : 'Adresse bestätigen'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="address-expand__row">
        <div className="filter-field" style={{ flex: 1 }}>
          <Input
            id="departure-date"
            type="date"
            value={filters.departure_date || ''}
            onChange={(e) => updateFilter('departure_date', e.target.value)}
            className="text__input"
          />
        </div>

        <div className="filter-field" style={{ flex: '0 0 7rem' }}>
          <TimeInput
            id="departure-time"
            value={filters.departure_time || ''}
            onChange={(e) => updateFilter('departure_time', e.target.value)}
            className="text__input"
          />
        </div>
      </div>
    </div>
  );
};

export default LocationFilter;
